<template>
  <PullToRefresh
    :onRefresh="loadHome"
    :pullText="$t('common.pull_to_refresh')"
    :releaseText="$t('common.release_to_refresh')"
    :refreshingText="$t('common.refreshing')"
  >
    <div class="home-page">
      <div class="container container--fluid">
        <header class="home-page__header">
          <div>
            <h1 class="home-page__title">
              {{ greeting }}
              <template v-if="firstName">, {{ firstName }}</template>
            </h1>
            <p class="home-page__subtitle">{{ todayFormatted }}</p>
          </div>
          <div class="home-page__header-actions">
            <Btn
              v-if="hasConversation"
              variant="tertiary"
              size="sm"
              icon="plus"
              @click="resetConversation"
            >
              {{ $t('home.chat.new_conversation') }}
            </Btn>
            <Btn
              v-else
              variant="tertiary"
              size="sm"
              icon="redo"
              :loading="loading && !data"
              class="home-page__refresh-btn"
              @click="loadHome"
            >
              {{ $t('common.refresh') }}
            </Btn>
          </div>
        </header>

        <section class="home-page__chat">
          <HomeChatComposer
            :placeholder="$t('home.chat.placeholder')"
            :sending="sending"
            :suggestions="hasConversation ? [] : suggestions"
            :compact="hasConversation"
            @submit="handleAsk"
          />
          <div v-if="!hasConversation" class="home-page__chat-meta">
            <span class="home-page__chat-badge">
              <BaseIcon name="wand" size="10" />
              Beta
            </span>
            <span class="home-page__chat-meta-text">
              {{ $t('home.chat.spike_notice') }}
            </span>
          </div>
        </section>

        <section v-if="hasConversation" class="home-page__thread chat-thread-scroll">
          <HomeChatThread
            :messages="messages"
            :sending="sending"
            @confirm-action="handleConfirmAction"
            @dismiss-action="handleDismissAction"
          />
        </section>

        <div v-if="hasConversation" class="home-page__overview-toggle">
          <button
            type="button"
            class="home-page__toggle-btn"
            @click="showCardsWhenChatting = !showCardsWhenChatting"
          >
            <BaseIcon name="chevron" size="10" :rotate="showCardsWhenChatting ? '180' : '0'" />
            <span>
              {{
                showCardsWhenChatting
                  ? $t('home.chat.hide_overview')
                  : $t('home.chat.show_overview')
              }}
            </span>
          </button>
        </div>

        <!-- ================== SUMMARY CARDS ================== -->
        <div v-show="!hasConversation || showCardsWhenChatting" class="home-page__grid">
          <!-- ============================ MIJN TAKEN ============================ -->
          <HomeSummaryCard
            :title="$t('home.tasks.title')"
            icon="check-square"
            tone="brand"
            :loading="loading && !data"
            :is-empty="!loading && tasks.length === 0"
            :empty-title="$t('home.tasks.empty_title')"
            :empty-description="$t('home.tasks.empty_description')"
            :count="data?.tasks?.total_open ?? null"
            :shown-count="tasks.length"
            view-all-route="/tasks"
            :view-all-label="$t('home.tasks.view_all')"
            always-show-view-all
            flush
          >
            <TaskListItem
              v-for="task in tasks"
              :key="task.id"
              :task="task"
              @updated="loadHome"
            />
          </HomeSummaryCard>

          <!-- ============================ TE VERSTUREN OFFERTES ============================ -->
          <HomeSummaryCard
            :title="$t('home.drafts.title')"
            icon="file"
            tone="amber"
            :loading="loading && !data"
            :is-empty="!loading && drafts.length === 0"
            :empty-title="$t('home.drafts.empty_title')"
            :empty-description="$t('home.drafts.empty_description')"
            :count="draftsTotal"
            :shown-count="drafts.length"
            view-all-route="/proposals?status=draft"
            :view-all-label="$t('home.drafts.view_all')"
            flush
          >
            <ProposalListItem v-for="draft in drafts" :key="draft.id" :proposal="draft" />
          </HomeSummaryCard>

          <!-- ============================ OPENSTAANDE OFFERTES ============================ -->
          <HomeSummaryCard
            :title="$t('home.proposals.title')"
            icon="file"
            tone="sky"
            :loading="loading && !data"
            :is-empty="!loading && proposals.length === 0"
            :empty-title="$t('home.proposals.empty_title')"
            :empty-description="$t('home.proposals.empty_description')"
            :count="proposalsTotal"
            :shown-count="proposals.length"
            view-all-route="/proposals?status=sent,viewed"
            :view-all-label="$t('home.proposals.view_all')"
            flush
          >
            <ProposalListItem
              v-for="proposal in proposals"
              :key="proposal.id"
              :proposal="proposal"
            />
          </HomeSummaryCard>
        </div>
      </div>
    </div>

  </PullToRefresh>
</template>

<script>
  import { ref, computed, onMounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import dayjs from 'dayjs';
  import 'dayjs/locale/nl';

  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';
  import { useAssistantStore } from '@/store/assistantStore';
  import { useI18n } from '@/composables/useI18n';
  import { dashboardService } from '@/services/dashboardService';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import PullToRefresh from '@/components/partials/PullToRefresh.vue';
  import HomeSummaryCard from '@/components/dashboard/HomeSummaryCard.vue';
  import HomeChatComposer from '@/components/dashboard/HomeChatComposer.vue';
  import HomeChatThread from '@/components/dashboard/HomeChatThread.vue';
  import TaskListItem from '@/components/partials/lists/TaskListItem.vue';
  import SubmissionListItem from '@/components/partials/lists/SubmissionListItem.vue';
  import ProposalListItem from '@/components/partials/lists/ProposalListItem.vue';
  import AppointmentListItem from '@/components/partials/lists/AppointmentListItem.vue';

  export default {
    name: 'HomePage',

    components: {
      Btn,
      BaseIcon,
      PullToRefresh,
      HomeSummaryCard,
      HomeChatComposer,
      HomeChatThread,
      TaskListItem,
      SubmissionListItem,
      ProposalListItem,
      AppointmentListItem,
    },

    setup() {
      const { t, locale } = useI18n();
      const userStore = useUserStore();
      const toastStore = useToastStore();
      const assistantStore = useAssistantStore();

      const { messages, sending } = storeToRefs(assistantStore);
      const hasConversation = computed(() => assistantStore.hasConversation);
      const showCardsWhenChatting = ref(false);

      const data = ref(null);
      const loading = ref(false);

      const suggestions = computed(() => [
        t('home.chat.suggestion_summary'),
        t('home.chat.suggestion_invoices'),
        t('home.chat.suggestion_proposals'),
        t('home.chat.suggestion_today'),
      ]);

      const handleAsk = async (text) => {
        try {
          await assistantStore.sendMessage(text);
        } catch (error) {
          // The store already pushes a friendly error message into the thread,
          // so we only surface a toast for non-rate-limit / non-validation errors
          // to avoid double notification noise.
          if (!['rate_limited', 'validation_error'].includes(error?.code)) {
            toastStore.addToast({ type: 'error', message: t('home.chat.send_failed') });
          }
        }
      };

      const handleConfirmAction = async (actionId) => {
        try {
          await assistantStore.confirmAction(actionId);
          toastStore.addToast({ type: 'success', message: t('home.chat.action.confirmed_toast') });
          // Refresh the dashboard summary in the background — most write-actions
          // change underlying data so the cards should reflect that.
          loadHome();
        } catch (error) {
          if (error?.code === 'action_expired') {
            toastStore.addToast({ type: 'info', message: t('home.chat.action.expired_toast') });
          } else {
            toastStore.addToast({ type: 'error', message: t('home.chat.action.confirm_failed') });
          }
        }
      };

      const handleDismissAction = (actionId) => {
        assistantStore.dismissAction(actionId);
      };

      const resetConversation = () => {
        assistantStore.reset();
        showCardsWhenChatting.value = false;
      };

      const firstName = computed(
        () => userStore.user?.user_metadata?.first_name || userStore.userData?.first_name || ''
      );

      const greeting = computed(() => {
        const hour = new Date().getHours();
        if (hour < 6) return t('home.greeting.night');
        if (hour < 12) return t('home.greeting.morning');
        if (hour < 18) return t('home.greeting.afternoon');
        return t('home.greeting.evening');
      });

      const todayFormatted = computed(() => {
        const d = dayjs().locale(locale.value === 'nl-NL' ? 'nl' : 'en');
        return d.format('dddd D MMMM YYYY');
      });

      const tasks = computed(() => data.value?.tasks?.items || []);
      const submissions = computed(() => data.value?.submissions?.items || []);
      const proposals = computed(() => data.value?.proposals?.items || []);
      const appointments = computed(() => data.value?.appointments?.items || []);

      const drafts = computed(() => data.value?.drafts?.items || []);
      const draftsTotal = computed(() => data.value?.drafts?.total_open ?? null);
      const proposalsTotal = computed(() => data.value?.proposals?.total_open ?? null);

      const loadHome = async () => {
        loading.value = true;
        try {
          data.value = await dashboardService.getHome();
        } catch (error) {
          console.error('Failed to load home dashboard:', error);
          toastStore.addToast({
            type: 'error',
            message: t('home.load_failed'),
          });
        } finally {
          loading.value = false;
        }
      };

      onMounted(loadHome);

      return {
        data,
        loading,
        loadHome,
        firstName,
        greeting,
        todayFormatted,
        tasks,
        submissions,
        proposals,
        appointments,
        drafts,
        draftsTotal,
        proposalsTotal,
        // Chat
        messages,
        sending,
        hasConversation,
        suggestions,
        showCardsWhenChatting,
        handleAsk,
        handleConfirmAction,
        handleDismissAction,
        resetConversation,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .home-page {
    &__refresh-btn {
      @include lg-down {
        display: none;
      }
    }
    &__header {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.5rem 0 1.25rem;

      @include lg-down {
        padding: 0.75rem 0 1rem;
      }
    }

    &__header-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--slate-900);

      @include lg-down {
        font-size: 1.25rem;
      }
    }

    &__subtitle {
      margin: 0.25rem 0 0;
      font-size: 0.875rem;
      color: var(--slate-500);
      text-transform: capitalize;
    }

    &__chat {
      margin-bottom: 1rem;
      padding: 0.875rem;
      border-radius: 1rem;
      border: 1px solid var(--slate-200);
      background: linear-gradient(180deg, #ffffff 0%, var(--slate-50) 100%);
      box-shadow: 0 1px 2px rgb(15 23 42 / 0.04);

      @include lg-down {
        margin: 0 -0.25rem 0.875rem;
        padding: 0.75rem;
        border-radius: 0.875rem;
      }
    }

    &__chat-meta {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.5rem;
      padding: 0 0.25rem;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__chat-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.0625rem 0.4375rem;
      border-radius: 9999px;
      background: linear-gradient(135deg, var(--brand-50), var(--violet-50, #f5f3ff));
      color: var(--brand-700);
      font-size: 0.625rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;

      .icon {
        color: var(--brand-500);
      }
    }

    &__chat-meta-text {
      @include ellipsis(1);
    }

    &__thread {
      max-height: 58vh;
      overflow-y: auto;
      padding: 0.75rem;
      margin-bottom: 0.875rem;
      border-radius: 1rem;
      border: 1px solid var(--slate-200);
      background: #ffffff;
      box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.7);

      @include lg-down {
        max-height: 52vh;
        padding: 0.625rem;
        border-radius: 0.875rem;
      }
    }

    &__overview-toggle {
      margin-bottom: 0.75rem;
      display: flex;
      justify-content: center;
    }

    &__toggle-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.875rem;
      border-radius: 9999px;
      border: 1px solid var(--slate-200);
      background: #ffffff;
      color: var(--slate-600);
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;

      @include hover {
        background: var(--slate-50);
        color: var(--slate-900);
      }

      .icon {
        transition: transform 0.2s ease;
      }
    }

    &__grid {
      display: grid;
      gap: 1rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
